import { ETAPAS, situacaoDa } from '../etapas'

/**
 * Espinha do dossiê — as sete etapas com nome, resumo e situação.
 *
 * Existe porque o cliente pediu ver o que já percorreu e o que falta. Uma barra
 * de progresso sozinha responde "quanto falta"; ela não responde "o que vem
 * depois", que é a pergunta que faz alguém abandonar um formulário longo por
 * não saber se vai precisar de documento que não tem à mão.
 *
 * No celular, sete linhas comeriam a tela inteira antes do primeiro campo —
 * então vira barra segmentada com o nome da etapa atual. A informação de
 * "o que vem depois" se perde ali, e é um custo consciente: espaço é mais
 * escasso que curiosidade numa tela de 360px.
 */

interface Props {
  atual: number
  /** Maior etapa já alcançada — o que o servidor devolveu em `etapaAtual`. */
  alcancada: number
  aoIr: (numero: number) => void
}

export function Espinha({ atual, alcancada, aoIr }: Props) {
  const etapaAtual = ETAPAS.find((e) => e.numero === atual)

  return (
    <>
      {/* --- celular: barra segmentada --- */}
      <nav className="trilha somente-compacto" aria-label="Progresso do preenchimento">
        <div className="trilha__barra" aria-hidden="true">
          {ETAPAS.map((e) => (
            <span
              key={e.numero}
              className={
                'trilha__segmento' +
                (e.numero < atual ? ' trilha__segmento--feita' : '') +
                (e.numero === atual ? ' trilha__segmento--atual' : '')
              }
            />
          ))}
        </div>
        <p className="trilha__rotulo">
          <span className="trilha__titulo">{etapaAtual?.titulo}</span>
          <span className="trilha__contagem">
            Etapa {atual} de {ETAPAS.length}
          </span>
        </p>
      </nav>

      {/* --- desktop: a espinha --- */}
      <nav className="espinha somente-amplo" aria-label="Etapas do pré-cadastro">
        <ol className="espinha__lista">
          {ETAPAS.map((etapa) => {
            const situacao = situacaoDa(etapa, atual, alcancada)
            const navegavel = situacao === 'concluida' || situacao === 'proxima'
            const conteudo = (
              <>
                <span className="espinha__numero" aria-hidden="true">
                  {String(etapa.numero).padStart(2, '0')}
                </span>
                <span>
                  <span className="espinha__titulo">
                    {etapa.titulo}
                    {situacao === 'concluida' ? <span className="espinha__marca"> ✓</span> : null}
                  </span>
                  <span className="espinha__resumo">{etapa.resumo}</span>
                </span>
              </>
            )

            return (
              <li
                key={etapa.numero}
                className={`espinha__item espinha__item--${situacao}`}
                aria-current={situacao === 'atual' ? 'step' : undefined}
              >
                {navegavel ? (
                  <button type="button" className="espinha__atalho" onClick={() => aoIr(etapa.numero)}>
                    {conteudo}
                    <span className="oculto-visual">
                      {situacao === 'concluida' ? ' — concluída, voltar' : ' — ir para esta etapa'}
                    </span>
                  </button>
                ) : (
                  <span className="espinha__estatico">
                    {conteudo}
                    {situacao === 'indisponivel' ? (
                      <span className="oculto-visual"> — ainda não disponível</span>
                    ) : null}
                  </span>
                )}
              </li>
            )
          })}
        </ol>
      </nav>
    </>
  )
}
